/** Ultra-detailed "How it works" content per SS-4.1 → 4.9 sub-module. */

export const HOW_IT_WORKS = {
  PLANNING: [
    { title: 'Define the sourcing objective & business case',
      body: 'Capture the high-level goal: are you sourcing a one-off purchase, building a multi-year framework, or running a strategic re-bid of an incumbent contract? The strategy choice cascades into evaluation criteria, supplier coverage, and even the legal route (open tender vs negotiated procedure).',
      steps: [
        'Set category, scope, geographic coverage, and estimated value.',
        'Define KPIs: target savings %, supplier diversity targets, sustainability thresholds.',
        'Identify stakeholders (business owner, legal, finance, sustainability, IT).',
        'Pick the procurement route — RFP / RFQ / eAuction / Direct Award / Framework.',
      ]},
    { title: 'Evaluation criteria & weights',
      body: 'Build a transparent weighted scorecard. Technical criteria typically carry 60–70% in services sourcing; commercial 30–40%. Add pass/fail gates for non-negotiable requirements like sanctions screening, financial health, or quality certifications.',
      steps: [
        'Add technical criteria (e.g., experience, methodology, key personnel).',
        'Add commercial criteria with the price-normalisation rule (cheapest = 100).',
        'Set pass/fail gates with the exact threshold.',
        'Configure tie-breaker rules.',
      ]},
    { title: 'Timeline & milestones',
      body: 'A robust event timeline includes draft → publish → questions deadline → submission deadline → opening → evaluation → award. Build in buffers for clarification cycles and unsolicited extensions.',
      steps: [
        'Set publication date.',
        'Set Q&A window (typically 30–50% of total event length).',
        'Set bid submission deadline (UTC with timezone shown to bidders).',
        'Set target award & contract execution dates.',
      ]},
    { title: 'Stakeholder approvals & DoA',
      body: 'The Delegation of Authority matrix determines who must approve the event before it can be published. The system routes the draft through the right approval ladder based on category and value bands.',
      steps: ['Submit DRAFT for approval review.', 'Approver(s) see all rationale + evaluation criteria.', 'On final approval, the system unlocks the "Continue to Invitation" CTA.'] },
  ],

  INVITATION: [
    { title: 'Build a qualified longlist',
      body: 'Start from existing supplier master, category-tagged history of prior events, and external discovery (BrasilAPI, OFAC, internal SRM). The longlist should be 3–5× the target shortlist size.',
      steps: ['Filter by category, geography, certification, prior performance.',
              'Auto-screen against sanctions lists (UN / OFAC / EU).',
              'Apply category diversity rules (e.g., at least one SME or local supplier).',
              'Review with category manager.'] },
    { title: 'Shortlist & invitation pack',
      body: 'The shortlist becomes the pool of suppliers who receive invitations. Each invitation pack contains the bid documents, the timeline, evaluation methodology summary, and the secure portal credentials.',
      steps: ['Move suppliers from longlist → shortlist.',
              'Generate per-supplier portal invitations (unique invite tokens).',
              'Dispatch via Resend (email) + Twilio (SMS) when integration is enabled.',
              'Track acknowledgements; chase non-responders automatically after 48h.'] },
    { title: 'Open the registration window',
      body: 'Once invitations are sent, suppliers self-register, accept the NDA, declare conflicts of interest, and confirm they intend to bid (or decline with reason). The buyer can adjust the shortlist mid-stream up until the clarification window opens.',
      steps: ['Monitor accept/decline rate.',
              'Top up the shortlist if too many declines.',
              'Advance to Clarification when the registration cut-off passes.'] },
  ],

  CLARIFICATION: [
    { title: 'Q&A intake & triage',
      body: 'All bidder questions arrive in a single inbox. The Clarification Concierge agent groups similar questions, suggests buyer responses, and flags any clarification that may require an addendum (i.e., changes to the bid documents themselves).',
      steps: ['Suppliers post questions via the portal.',
              'Concierge agent triages and de-duplicates.',
              'Buyer drafts response, citing exact paragraph of the bid documents.',
              'On publish: response broadcasts to ALL invited suppliers simultaneously.'] },
    { title: 'Pre-bid meeting (optional)',
      body: 'For complex events, host a live pre-bid meeting (in-person or video). The system records attendance, captures the verbatim transcript, and publishes the minutes as part of the official bid pack.',
      steps: ['Schedule meeting; auto-invite all shortlisted suppliers.',
              'Record minutes inside the event.',
              'Issue any commitments as official addenda.'] },
    { title: 'Addenda & deadline management',
      body: 'If a clarification materially changes a requirement, the buyer issues an addendum. Significant addenda automatically extend the submission deadline (configurable, default +3 days).',
      steps: ['Mark a clarification as MATERIAL_CHANGE.',
              'System creates a versioned addendum (Addendum #1, #2, …).',
              'Bidder portal forces re-acknowledgement of the latest addendum before they can submit.'] },
  ],

  BIDDING: [
    { title: 'Sealed two-envelope submission',
      body: 'Suppliers upload Technical and Commercial envelopes separately. Each is encrypted with Fernet at rest. The commercial envelope is sealed until Bid Opening (SS-4.5) so the technical evaluation cannot be biased by knowledge of price.',
      steps: ['Supplier uploads technical PDFs + structured form responses.',
              'Supplier uploads commercial schedule (totals + line items).',
              'System hashes the documents and stores the hash chain.',
              'Submission is timestamped; late submissions are rejected automatically.'] },
    { title: 'Watchdog monitoring',
      body: 'The Submission Watchdog agent reports who has submitted, who is in-progress, and who has not started. The buyer can dispatch follow-up nudges or grant micro-extensions to specific suppliers if justified.',
      steps: ['Live dashboard shows registration → preparation → submitted counts.',
              'Auto-nudge 24h and 4h before deadline.',
              'Buyer may grant individual extension (audit-logged).'] },
    { title: 'Auto-close at deadline',
      body: 'At the submission deadline, the system snapshots all received bids, locks new uploads, and transitions the event to "BID_SUBMISSION_CLOSED" — making the bids ready for opening.',
      steps: ['Hard close at deadline timestamp.',
              'Generate audit certificate listing every received bid + hash.',
              '"Continue to Opening" CTA appears.'] },
  ],

  OPENING: [
    { title: 'Quorum & unsealing ceremony',
      body: 'Bid Opening requires a defined quorum (typically 2–3 authorised members, depending on policy). The system records who clicked "Open" and at what time, locks the action against tampering, and reveals the technical envelopes first.',
      steps: ['Each quorum member logs in and approves the opening.',
              'Once quorum is reached, technical envelopes unseal.',
              'Commercial envelopes remain sealed until technical evaluation finishes.'] },
    { title: 'Anomaly & arithmetic checks',
      body: 'Auto-validation flags abnormally low bids (>30% below estimate), missing mandatory documents, currency mismatches, and basic arithmetic errors in the price schedule.',
      steps: ['Run validation suite.',
              'Buyer reviews each anomaly and tags as ACCEPT or DISCLAIM.',
              'Disclaimed bids are documented in the opening minutes.'] },
    { title: 'Opening minutes & sign-off',
      body: 'The minutes are auto-generated from the opening events, signed by quorum members, and attached to the event as a formal artefact.',
      steps: ['Auto-generated minutes draft.',
              'Quorum members sign (e-signature).',
              'Minutes are filed and emailed to all bidders.'] },
  ],

  EVALUATION: [
    { title: 'Evaluator assignment & calibration',
      body: 'Assign evaluators per technical criterion. For high-stakes events, run a calibration session where evaluators score a sample bid together to align rubric interpretation.',
      steps: ['Add evaluators with criterion-level access.',
              'Calibrate (optional).',
              'Lock the rubric before scoring opens.'] },
    { title: 'Parallel scoring & moderation',
      body: 'Each evaluator scores independently. The Evaluation Coach agent surfaces outliers (e.g., one evaluator scoring 30 while peers score 70) and recommends moderation. A moderation session resolves outliers into a consensus score.',
      steps: ['Evaluators submit raw scores.',
              'Coach agent identifies outliers.',
              'Moderator opens a moderation thread per criterion.',
              'Consensus scores are stored alongside raw scores for audit.'] },
    { title: 'Commercial unsealing & aggregation',
      body: 'Once the technical phase is finalised, the commercial envelopes are unsealed. The system normalises prices (lowest = 100) and computes weighted overall scores per the configured methodology.',
      steps: ['Unlock commercial envelopes (DoA-gated).',
              'Apply price normalisation.',
              'Compute weighted overall ranks.',
              '"Continue to eAuction / BAFO / Award" CTA appears.'] },
  ],

  AUCTION: [
    { title: 'Configure auction rules',
      body: 'Pick REVERSE or DUTCH. For REVERSE: set decrement rules (FIXED $ or PERCENTAGE), anti-sniping window (5min before close + +3min extension), visibility mode (RANK_ONLY / FULL_PRICE), and multi-award allocation (single winner or EQUAL_SPLIT/PRO_RATA).',
      steps: ['Choose auction type.',
              'Set per-lot starting & reserve prices.',
              'Configure decrement rule.',
              'Define visibility & extension policy.'] },
    { title: 'Lobby & participant briefing',
      body: 'Before going LIVE, host a brief lobby session where suppliers join, run practice bids, and confirm their browser/connectivity. The Auction Strategist agent surfaces likely savings ranges based on prior auctions in the category.',
      steps: ['Open lobby 15 min before scheduled start.',
              'Run practice mode (bids are not persisted).',
              'Verify all suppliers are connected.',
              'Click "Start" to go LIVE.'] },
    { title: 'Live bidding & extension dynamics',
      body: 'Bids must respect the decrement rule. Any bid placed within the anti-sniping window automatically extends the lot end-time. The cryptographic bid-log chain (SHA-256 linked) provides tamper-evident audit.',
      steps: ['Suppliers place bids; rankings recompute live.',
              'Last-minute bids trigger extensions.',
              'Lots close one by one as their timers expire.',
              'Buyer seals the bid-log chain at close.'] },
  ],

  BAFO: [
    { title: 'Shortlist & feedback',
      body: 'Take the top N from SS-4.6 (default 3) and craft tailored feedback per supplier — what to keep, what to improve. The BAFO Negotiator agent drafts the feedback narrative.',
      steps: ['Pick shortlist size & methodology (PRICE_ONLY or FULL_RESCORE).',
              'Write per-supplier feedback.',
              'Dispatch invitations with deadline.'] },
    { title: 'Multi-round revision',
      body: 'BAFO supports multiple rounds. Each round opens with a deadline; the system auto-seals when the deadline passes. Suppliers can withdraw at any time with documented reason.',
      steps: ['Open Round #1.',
              'Suppliers submit revised offers via portal (Fernet-encrypted).',
              'Auto-seal at deadline OR manually close.',
              'Open subsequent rounds if needed.'] },
    { title: 'Side-by-side comparison',
      body: 'The system shows initial vs each BAFO round, per-line-item drilldown, and the new commercial score. The Coach can run a FULL_RESCORE to re-apply the original methodology with the new prices.',
      steps: ['View initial vs BAFO side-by-side.',
              'Drill into line items.',
              'Optional: run Full Re-score.',
              'Generate award recommendation.'] },
  ],

  FRAMEWORK: [
    { title: 'Activate the framework',
      body: 'A framework is born from an awarded SS-4.6/4.7/4.8 outcome. Set the term, the ranking mechanism per lot (FIXED / CASCADING / ROTATION / MINI_COMPETITION), the call-off threshold for mini-comp, and cap controls.',
      steps: ['Choose term (24 / 36 / 48 months).',
              'Assign appointed suppliers per lot with ranks.',
              'Upload negotiated pricing schedule.',
              'Configure utilisation cap + hard-stop policy.'] },
    { title: 'Call-off lifecycle',
      body: 'Business users raise call-offs against the framework. The system auto-selects the supplier per the configured mechanism: FIXED → rank #1; CASCADING → rank #1, falls back on decline; ROTATION → round-robin; MINI_COMPETITION → mini-tender among all appointed suppliers when value exceeds threshold.',
      steps: ['User raises call-off with line items.',
              'System selects supplier per mechanism.',
              'For cascading: supplier accepts or declines → next supplier.',
              'For mini-comp: supplier portal opens, prices compared, winner selected.'] },
    { title: 'Performance & lifecycle management',
      body: 'Track utilisation against the cap, run quarterly performance scorecards (quality / on-time / responsiveness), and watch the expiry timeline. 90 days before expiry, the system auto-creates a renewal DRAFT.',
      steps: ['Monitor utilisation in real-time.',
              'Capture scorecards quarterly.',
              'Issue amendments (price / scope / supplier) as needed.',
              'Auto-renewal DRAFT 90d before expiry.'] },
  ],
};

/** Map module-key → AI agent module key for the chat panel. */
export const AGENT_MAP = {
  PLANNING:      { module: 'ss41', name: 'Strategy Architect Agent' },
  INVITATION:    { module: 'ss42', name: 'Supplier Scout Agent' },
  CLARIFICATION: { module: 'ss43', name: 'Clarification Concierge Agent' },
  BIDDING:       { module: 'ss44', name: 'Submission Watchdog Agent' },
  OPENING:       { module: 'ss45', name: 'Bid Opening Auditor Agent' },
  EVALUATION:    { module: 'ss46', name: 'Evaluation Coach Agent' },
  AUCTION:       { module: 'ss47', name: 'Auction Strategist Agent' },
  BAFO:          { module: 'ss48', name: 'BAFO Negotiator Agent' },
  FRAMEWORK:     { module: 'ss49', name: 'Framework Steward Agent' },
  DIRECT_AWARD:  { module: 'ss410', name: 'Direct Award Justifier Agent' },
  RECOMMENDATION:{ module: 'ss411', name: 'Award Recommender Agent' },
  CLOSURE:       { module: 'ss412', name: 'Award Closure & Comms Agent' },
  COMMUNICATION: { module: 'ss413', name: 'Communication Concierge Agent' },
  ANALYTICS:     { module: 'ss414', name: 'Sourcing Analytics Storyteller Agent' },
  PORTAL:        { module: 'ss415', name: 'Supplier Portal Steward Agent' },
  COMPLIANCE:    { module: 'ss416', name: 'ITT Compliance Sentinel Agent' },
};
